import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BadgeDollarSign, FileDown, FileText, Lock, Unlock, AlertTriangle } from "lucide-react";
import { http, downloadUrl, toastApiError } from "../lib/api";
import { toast } from "../lib/notify";
import { fmtTL } from "../lib/format";
import { safePath } from "../lib/nav";
import { useAuth } from "../context/AuthContext";
import { periodLabel } from "../components/claims/claimGrouping";
import { PAYROLL_ROLES } from "../lib/roles";
import PayrollTable from "../components/payroll/PayrollTable";
import PayrollProjects from "../components/payroll/PayrollProjects";
import PayrollParams from "../components/payroll/PayrollParams";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const sel = "px-2.5 py-1.5 text-xs border border-slate-300 rounded-md bg-white";

const TONES = { slate: "border-slate-200 text-slate-900", red: "border-red-200 text-red-900", emerald: "border-emerald-200 text-emerald-900", violet: "border-violet-200 text-violet-900" };
function Stat({ label, value, tone = "slate", testid }) {
  const [border, text] = TONES[tone].split(" ");
  return (
    <div className={`bg-white border rounded-lg px-4 py-3 ${border}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-bold font-mono ${text}`}>{value}</div>
    </div>
  );
}

// Operasyon → Bordro: dönem seç → tüm personel tek tabloda (gün, brüt, SGK, GV, DV, net, işveren maliyeti) · Excel · zarf PDF · proje bazlı maliyet · Bordroyu Kapat
export default function Bordro() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(params.get("donem") || "");
  const [periods, setPeriods] = useState([]);
  const [projectId, setProjectId] = useState(params.get("proje") || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const returnTo = safePath(params.get("don"));
  useEffect(() => {
    http.get("/payroll/periods").then((r) => { setPeriods(r.data); setPeriod((p) => p || r.data[0]?.period || thisMonth()); }).catch(() => setPeriod((p) => p || thisMonth()));
  }, []);
  const reloadPeriods = () => http.get("/payroll/periods").then((r) => setPeriods(r.data)).catch(() => {});
  const allowed = !user || PAYROLL_ROLES.includes(user.role);
  const load = useCallback(() => {
    if (!period || !allowed) return;
    setData(null);
    http.get("/payroll", { params: { period, ...(projectId ? { project_id: projectId } : {}) } }).then((r) => setData(r.data)).catch((e) => { toastApiError(e); setData({ rows: [], totals: {}, projects: [], warnings: [] }); });
  }, [period, projectId, allowed]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (period) setParams((s) => { s.set("donem", period); if (projectId) s.set("proje", projectId); else s.delete("proje"); return s; }, { replace: true }); }, [period, projectId, setParams]);
  useEffect(() => {
    if (!returnTo) return;
    const onKey = (e) => { if (e.key === "Escape" && !e.target.closest("input,select,textarea")) navigate(returnTo); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [returnTo, navigate]);
  const projectOptions = useMemo(() => (data?.projects || []).map((g) => ({ value: g.project_id, label: g.project_name })), [data]);
  const closed = data?.status === "closed";
  const qs = `period=${period}${projectId ? `&project_id=${projectId}` : ""}`;

  const closePayroll = async () => {
    if (!window.confirm(`${periodLabel(period)} bordrosu kapatılsın mı?\n\n${data?.totals?.people || 0} personel · net ${fmtTL(data?.totals?.net || 0)} · işveren maliyeti ${fmtTL(data?.totals?.employer_cost || 0)}\n\nKapatılınca satırlar saklanır; bu dönem için puantaj değişikliği engellenir (açmak için Yönetici → Bordroyu Aç).`)) return;
    setBusy(true);
    try { const { data: r } = await http.post(`/payroll/${period}/close`); toast.success(`${periodLabel(period)} bordrosu kapatıldı — ${r.people} personel · net ${fmtTL(r.net)}`); load(); reloadPeriods(); } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const reopenPayroll = async () => {
    if (!window.confirm(`${periodLabel(period)} bordrosu yeniden açılsın mı?\n\nSatırlar canlı hesaplanır; puantaj ve ücret değişikliklerine izin verilir.`)) return;
    setBusy(true);
    try { await http.post(`/payroll/${period}/reopen`); toast.success(`${periodLabel(period)} bordrosu açıldı`); load(); reloadPeriods(); } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const t = data?.totals || {};
  if (!allowed) {
    return (
      <div className="space-y-5" data-testid="page-bordro">
        <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-3"><BadgeDollarSign className="w-7 h-7 text-slate-700" /> Bordro</h1>
        <div className="bg-white border border-slate-200 rounded-lg px-5 py-10 text-center" data-testid="payroll-forbidden">
          <Lock className="w-8 h-8 mx-auto text-slate-300" />
          <div className="mt-3 text-sm font-semibold text-slate-800">Bordro için Yönetici, Muhasebe veya İK yetkisi gerekiyor</div>
          <div className="mt-1 text-xs text-slate-500">Rolünüz: {user?.role}. Erişim için yöneticinize başvurun.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="page-bordro">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-3"><BadgeDollarSign className="w-7 h-7 text-slate-700" /> Bordro</h1>
          <p className="text-sm text-slate-500 mt-1">Puantaj + ücret kartı + SGK oranları + gelir vergisi dilimlerinden aylık bordro. {returnTo && <span className="ml-1 px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px] font-semibold">ESC ile geri dön</span>}</p>
        </div>
        {data && (
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${closed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="payroll-status" title={closed ? `Kapatan: ${data.closed_by || ""} · ${data.closed_at ? new Date(data.closed_at).toLocaleString("tr-TR") : ""}` : "Canlı hesap — puantaj / ücret değişince güncellenir"}>
            {closed ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />} {closed ? "Kapalı" : "Taslak (açık)"}
          </span>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="payroll-toolbar">
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dönem
          <input type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} className={`${sel} block font-semibold`} data-testid="payroll-period" />
          {periods.length > 0 && <div className="mt-1 flex flex-wrap gap-1 normal-case tracking-normal">
            {periods.slice(0, 6).map((p) => <button key={p.period} type="button" onClick={() => setPeriod(p.period)} data-testid={`payroll-period-chip-${p.period}`}
              className={`px-1.5 py-0.5 rounded text-[10px] border ${p.period === period ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{p.period}{p.closed ? " 🔒" : ""}</button>)}
          </div>}
        </label>
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Proje
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={`${sel} block min-w-[220px]`} data-testid="payroll-project">
            <option value="">Tüm projeler</option>
            {projectOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <a href={downloadUrl(`/payroll/export?${qs}`)} target="_blank" rel="noreferrer" className="excel-btn" data-testid="payroll-export"><FileDown className="w-4 h-4" /> Excel{"\u2019"}e Aktar</a>
          <a href={downloadUrl(`/payroll/${period}/slips${projectId ? `?project_id=${projectId}` : ""}`)} target="_blank" rel="noreferrer" data-testid="payroll-slips-all"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-red-700 text-white text-sm font-semibold hover:bg-red-800 transition-colors"><FileText className="w-4 h-4" /> Toplu Zarf PDF</a>
          {!closed && !data?.year_closed && <button type="button" onClick={closePayroll} disabled={busy || !data || !(data.rows || []).length || !!projectId} data-testid="payroll-close" title={projectId ? "Kapatma tüm projeler için yapılır — proje filtresini kaldırın" : "Dönemi kapat ve kilitle"}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"><Lock className="w-4 h-4" /> Bordroyu Kapat</button>}
          {closed && !data?.year_closed && user?.role === "admin" && <button type="button" onClick={reopenPayroll} disabled={busy} data-testid="payroll-reopen"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-slate-300 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50 transition-colors"><Unlock className="w-4 h-4" /> Bordroyu Aç</button>}
          {data?.year_closed && <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs font-semibold" data-testid="payroll-year-locked" title="Muhasebe › Dönem Sonu'ndan yıl kapanışı geri alınmadan bordro değiştirilemez"><Lock className="w-3.5 h-3.5" /> {period.slice(0, 4)} yılı kapalı</span>}
        </div>
      </div>

      {data?.warnings?.map((w, i) => <div key={i} className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`payroll-warning-${i}`}><AlertTriangle className="w-4 h-4 shrink-0" /> {w}</div>)}
      {data === null && <div className="text-xs text-slate-400">Hesaplanıyor…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="payroll-stats">
            <Stat label="Personel" value={t.people || 0} testid="payroll-stat-people" />
            <Stat label="Toplam Brüt" value={fmtTL(t.gross || 0)} testid="payroll-stat-gross" />
            <Stat label="Kesintiler" value={fmtTL(t.deductions || 0)} tone="red" testid="payroll-stat-deductions" />
            <Stat label="Net Ödenen" value={fmtTL(t.net || 0)} tone="emerald" testid="payroll-stat-net" />
            <Stat label="İşveren Maliyeti" value={fmtTL(t.employer_cost || 0)} tone="violet" testid="payroll-stat-cost" />
          </div>
          <PayrollParams params={data.params} sgk={data.sgk} />
          <PayrollTable data={data} period={period} />
          <PayrollProjects projects={data.projects} period={period} picked={projectId} onPick={setProjectId} />
        </>
      )}
    </div>
  );
}
